import { ref } from 'vue'

export type DashboardLevel = 'none' | 'intermediate' | 'full'
export type PlanCode = 'free' | 'start' | 'pro' | 'business'

/**
 * ¿El nombre corresponde a uno de los cuatro planes del catálogo público?
 *
 * Devuelve null para cualquier otro. Un plan fuera de esa lista no es un error:
 * es un plan que Enlace creó y asignó a mano (los que no salen en la landing),
 * y el llamador decide qué hacer con él en vez de recibirlo disfrazado de Free.
 */
export function planNameToCode(name?: string | null): PlanCode | null {
	const n = (name ?? '').toLowerCase().trim()
	if (n === 'business+' || n === 'business') return 'business'
	if (n === 'pro') return 'pro'
	if (n === 'start') return 'start'
	if (n === 'free') return 'free'
	return null
}

/** Igual que planNameToCode pero cae a 'free', para comparar rangos del catálogo. */
export function planNameToPublicCode(name?: string | null): PlanCode {
	return planNameToCode(name) ?? 'free'
}

/**
 * Cuánto dashboard puede personalizar la empresa, decidido por el CÓDIGO del plan.
 *
 * El código y no el nombre: el nombre es texto editable desde el backoffice, así
 * que renombrar "PRO" movería permisos sin que nadie lo pida. El código es el
 * identificador estable — es el mismo criterio que ya usa el enforcement de
 * planes.
 *
 * Un código que no es del catálogo público recibe nivel completo. Es un plan que
 * creamos y asignamos a mano, y asignarlo es dar acceso: ante algo que no
 * reconocemos, el error barato es de más, no de menos. (Antes caía en 'free' y
 * por lo tanto en 'none', y una empresa con plan sin límites terminaba con el
 * dashboard más restringido de todos.)
 */
export function dashboardLevelForCode(code?: string | null): DashboardLevel {
	if (!code) return 'none'
	if (code === 'business') return 'full'
	if (code === 'pro') return 'intermediate'
	if (code === 'start' || code === 'free') return 'none'

	return 'full'
}

/**
 * Respaldo por nombre, para cuando todavía no hay código a la mano.
 *
 * Sólo reconoce los nombres del catálogo público tal como los sembró el seeder;
 * un nombre editado deja de hacer match y cae en 'none' — conservador a
 * propósito, porque un nombre no es evidencia de nada. Preferir
 * `dashboardLevelForCode` siempre que el código esté disponible.
 */
export function getDashboardLevel(plan?: string | null): DashboardLevel {
	return dashboardLevelForCode(planNameToCode(plan))
}

export interface GridItemDef {
	id: string
	colSpan: number
	fixed?: boolean
}

export interface LayoutTemplate {
	id: string
	name: string
	description: string
	layout: GridItemDef[]
}

// v8: avisos sube a la primera fila, antes que las métricas. En v7 quedaba después
// de credit/stats/actions/bookings y seguía pidiendo un scroll mínimo; ahora es lo
// primero que se ve al entrar al tablero. Subir el sufijo invalida los layouts
// guardados de todos los usuarios, y es lo correcto aquí: un layout viejo lo deja
// donde estaba antes.
const STORAGE_KEY = 'dashboard-widget-layout-v8'

export const defaultLayout: GridItemDef[] = [
	// Avisos va fijo arriba de todo: es el mismo lugar que el panel de redacción
	// llama "carrusel", y mezclarlo entre las columnas de métricas lo dejaría
	// pareciendo un widget más en vez del banner que anuncia promos y avisos.
	{ id: 'announcements',     colSpan: 12, fixed: true },
	// Fila 1: 4 widgets
	{ id: 'credit',            colSpan: 3 },
	{ id: 'stats',             colSpan: 3 },
	{ id: 'actions',           colSpan: 3 },
	{ id: 'bookings',          colSpan: 3 },
	// Fila 3: 1 expandible
	{ id: 'subscription',      colSpan: 12, fixed: true },
	// Fila 4: 4 widgets
	{ id: 'approvals',         colSpan: 3 },
	{ id: 'pending-requests',  colSpan: 3 },
	{ id: 'hotels',            colSpan: 3 },
	{ id: 'flights',           colSpan: 3 },
	// Fila 5: 1 expandible
	{ id: 'travelers',         colSpan: 12 },
	{ id: 'invoices',          colSpan: 12 },
]

export const layoutTemplates: LayoutTemplate[] = [
	{
		id: 'default',
		name: 'Equilibrado',
		description: '4 métricas arriba, 3 secciones abajo',
		layout: [
			{ id: 'announcements',    colSpan: 12, fixed: true },
			{ id: 'credit',           colSpan: 3 },
			{ id: 'stats',            colSpan: 3 },
			{ id: 'actions',          colSpan: 3 },
			{ id: 'bookings',         colSpan: 3 },
			{ id: 'subscription',     colSpan: 12, fixed: true },
			{ id: 'approvals',        colSpan: 3 },
			{ id: 'pending-requests', colSpan: 3 },
			{ id: 'hotels',           colSpan: 3 },
			{ id: 'flights',          colSpan: 3 },
			{ id: 'travelers',        colSpan: 12 },
			{ id: 'invoices',         colSpan: 12 },
		],
	},
	{
		id: 'ejecutivo',
		name: 'Ejecutivo',
		description: 'KPIs amplios arriba, operaciones abajo',
		layout: [
			{ id: 'announcements', colSpan: 12, fixed: true },
			{ id: 'credit',       colSpan: 6 },
			{ id: 'stats',        colSpan: 6 },
			{ id: 'subscription', colSpan: 12, fixed: true },
			{ id: 'approvals',    colSpan: 4 },
			{ id: 'bookings',     colSpan: 4 },
			{ id: 'actions',      colSpan: 4 },
			{ id: 'hotels',       colSpan: 6 },
			{ id: 'flights',      colSpan: 6 },
			{ id: 'travelers',    colSpan: 12 },
			{ id: 'invoices',     colSpan: 12 },
		],
	},
	{
		id: 'viajero',
		name: 'Viajero',
		description: 'Hoteles y vuelos al frente',
		layout: [
			// Al viajero los avisos le van arriba: las promociones son para él, y en esta
			// plantilla lo que interesa es lo que puede reservar hoy.
			{ id: 'announcements', colSpan: 12, fixed: true },
			{ id: 'hotels',       colSpan: 4 },
			{ id: 'flights',      colSpan: 4 },
			{ id: 'bookings',     colSpan: 4 },
			{ id: 'actions',      colSpan: 6 },
			{ id: 'approvals',    colSpan: 6 },
			{ id: 'subscription', colSpan: 12, fixed: true },
			{ id: 'credit',       colSpan: 6 },
			{ id: 'stats',        colSpan: 6 },
			{ id: 'travelers',    colSpan: 12 },
		],
	},
	{
		id: 'compacto',
		name: 'Compacto',
		description: '2 columnas, vista condensada',
		layout: [
			{ id: 'announcements', colSpan: 12, fixed: true },
			{ id: 'credit',       colSpan: 6 },
			{ id: 'stats',        colSpan: 6 },
			{ id: 'actions',      colSpan: 6 },
			{ id: 'bookings',     colSpan: 6 },
			{ id: 'subscription', colSpan: 12, fixed: true },
			{ id: 'approvals',    colSpan: 6 },
			{ id: 'hotels',       colSpan: 6 },
			{ id: 'flights',      colSpan: 6 },
			{ id: 'travelers',    colSpan: 6 },
		],
	},
	{
		id: 'cuadricula',
		name: 'Cuadrícula',
		description: '3 columnas uniformes en todo el dashboard',
		layout: [
			{ id: 'announcements', colSpan: 12, fixed: true },
			{ id: 'credit',       colSpan: 4 },
			{ id: 'stats',        colSpan: 4 },
			{ id: 'actions',      colSpan: 4 },
			{ id: 'approvals',    colSpan: 4 },
			{ id: 'bookings',     colSpan: 4 },
			{ id: 'hotels',       colSpan: 4 },
			{ id: 'subscription', colSpan: 12, fixed: true },
			{ id: 'flights',      colSpan: 4 },
			{ id: 'travelers',    colSpan: 8 },
		],
	},
	{
		id: 'mosaico',
		name: 'Mosaico',
		description: 'Tamaños variados, máxima densidad',
		layout: [
			{ id: 'announcements', colSpan: 12, fixed: true },
			{ id: 'hotels',       colSpan: 3 },
			{ id: 'flights',      colSpan: 3 },
			{ id: 'bookings',     colSpan: 3 },
			{ id: 'actions',      colSpan: 3 },
			{ id: 'credit',       colSpan: 5 },
			{ id: 'approvals',    colSpan: 7 },
			{ id: 'subscription', colSpan: 12, fixed: true },
			{ id: 'stats',        colSpan: 4 },
			{ id: 'travelers',    colSpan: 8 },
		],
	},
]

export function useWidgetGrid() {
	const loadLayout = (): GridItemDef[] => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY)
			if (!saved) return [...defaultLayout]
			const parsed: GridItemDef[] = JSON.parse(saved)
			// Restore order AND colSpans; fill in any widgets added since last save
			const allIds = new Set(defaultLayout.map((d) => d.id))
			const valid = parsed.filter((p) => allIds.has(p.id))
			const savedIds = new Set(valid.map((v) => v.id))
			const missing = defaultLayout.filter((d) => !savedIds.has(d.id))
			return [...valid, ...missing]
		} catch {
			return [...defaultLayout]
		}
	}

	const saveLayout = (items: GridItemDef[]) => {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
	}

	const resetLayout = () => {
		localStorage.removeItem(STORAGE_KEY)
		layout.value = [...defaultLayout]
	}

	const applyTemplate = (templateLayout: GridItemDef[]) => {
		layout.value = [...templateLayout]
		saveLayout(templateLayout)
	}

	const layout = ref<GridItemDef[]>(loadLayout())

	return { layout, saveLayout, resetLayout, applyTemplate }
}
